import { PAID_VERIFY_SOURCE_DETAIL } from '../../constants/verify'

export const MANUAL_REVIEW_MAX_PHOTO_COUNT = 100
/** 从支付订单创建成功开始，最多等待用户支付 30 分钟。 */
export const MANUAL_REVIEW_PAYMENT_POLLING_WINDOW_MS = 30 * 60 * 1000

/**
 * 已创建订单但尚未完成支付的人工验真会话。
 *
 * 订单创建后，用户仍可能在结果页点击其他照片或批次入口；因此后续提交复核时
 * 必须使用这份不可变快照，而不是读取界面中可继续变化的选择状态。
 */
export interface ManualReviewPaymentSession {
  id: string
  photoUrls: string[]
  pageSource: string
}

/** 支付轮询的订单归属状态，用于拒绝重复或已过期的异步响应。 */
export interface ManualReviewPaymentPollState {
  activeOrderNo: string
  settledOrderNo: string
}

export const canSettleManualReviewPaymentOrder = (
  pollState: ManualReviewPaymentPollState,
  orderNo: string
) =>
  Boolean(orderNo) &&
  pollState.activeOrderNo === orderNo &&
  pollState.settledOrderNo !== orderNo

export const createManualReviewPaymentDeadline = (createdAt: number) =>
  createdAt + MANUAL_REVIEW_PAYMENT_POLLING_WINDOW_MS

export const isManualReviewPaymentPollingExpired = (
  deadlineAt: number,
  now: number
) => deadlineAt > 0 && now >= deadlineAt

/** 截止后没有在途查单时，补一次最终查单再结算超时。 */
export const shouldRunManualReviewPaymentFinalStatusQuery = (
  isQueryInFlight: boolean
) => !isQueryInFlight

/** 只允许最新一次建单请求的异步响应更新当前支付会话。 */
export const isManualReviewPaymentRequestCurrent = (
  requestGeneration: number,
  currentGeneration: number
) => requestGeneration === currentGeneration

export const createManualReviewPaymentSession = (
  photos: { photoUrl: string }[],
  pageSource: string
): ManualReviewPaymentSession => ({
  id: `manual-review-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  photoUrls: photos.map((photo) => photo.photoUrl),
  pageSource
})

/**
 * 仅当入口和全部照片快照都相同时，才能复用尚在支付中的订单。
 * 同一张数但照片不同，也必须创建新的订单，避免复核申请与已支付订单错配。
 */
export const findManualReviewPaymentSessionBySnapshot = (
  sessions: ManualReviewPaymentSession[],
  photos: { photoUrl: string }[],
  pageSource: string
) => {
  const photoUrls = photos.map((photo) => photo.photoUrl)
  return sessions.find(
    (session) =>
      session.pageSource === pageSource &&
      session.photoUrls.length === photoUrls.length &&
      session.photoUrls.every(
        (photoUrl, index) => photoUrl === photoUrls[index]
      )
  )
}

export const validateManualReviewPhotoUrls = (photoUrls: string[]) => {
  if (
    photoUrls.length < 1 ||
    photoUrls.length > MANUAL_REVIEW_MAX_PHOTO_COUNT
  ) {
    return {
      valid: false as const,
      message: `单次人工验真最多支持${MANUAL_REVIEW_MAX_PHOTO_COUNT}张照片`
    }
  }
  return { valid: true as const }
}

export const buildManualReviewPrecreatePayload = (
  orderNo: string,
  questionDescription: string,
  pageSource: string,
  photoUrls: string[]
) => ({
  orderNo,
  questionDescription,
  pageSource,
  photoUrls
})

/**
 * @deprecated 仅兼容历史纯函数调用；人工验真付款不得再使用此载荷。
 * 新订单必须使用 buildPaidVerifyBatchOrderPayload，由订单中心按商品数量结算。
 */
export const buildManualReviewOrderExtraParams = (
  taskID: string | undefined,
  photoUrls: string[]
) => ({
  taskID,
  photoUrls,
  sourceDetail: PAID_VERIFY_SOURCE_DETAIL
})

/**
 * @deprecated 仅兼容历史纯函数调用；当前人工验真付款不会调用该函数。
 * 浏览器传入的总价不能作为订单结算依据。
 */
export const buildPaidVerifyOrderPayload = (
  goodsID: string,
  price: number,
  channel: number,
  extraOrderParams: Record<string, unknown> = {}
) => ({
  ...extraOrderParams,
  goodsID,
  channel,
  price
})

/**
 * 人工验真使用固定单价商品批量下单，订单中心按 goodsID × cnt 结算。
 * 价格只由商品中心配置决定，不接受浏览器传入的总价。
 */
export const buildPaidVerifyBatchOrderPayload = (
  goodsID: string,
  count: number,
  channel: number,
  groupID: string
) => ({
  goodsInfo: [{ goodsID, cnt: count }],
  channel,
  groupID,
  sourceDetail: PAID_VERIFY_SOURCE_DETAIL
})

/** 批量商品订单必须以 isBatch 查询支付状态。 */
export const buildPaidVerifyBatchOrderQueryPayload = (orderNo: string) => ({
  orderNo,
  isBatch: true
})

export const getManualReviewPushTaskId = (data: unknown) => {
  const pushTaskId = (data as { pushTaskId?: unknown } | undefined)?.pushTaskId
  return typeof pushTaskId === 'string' ? pushTaskId : ''
}

export const getManualReviewRouteId = (search: string, key: string) => {
  return new URLSearchParams(search).get(key) || ''
}

/** 支付成功后自动提交复核申请的重试参数：支付刚完成时网络通畅，重试只为覆盖瞬时抖动 */
export const AUTO_SUBMIT_RETRY_COUNT = 3
export const AUTO_SUBMIT_RETRY_DELAY = 2000

/**
 * 带重试的自动提交封装：action 失败后间隔 delayMs 再试，共尝试 retryCount 次；
 * 全部失败时抛出最后一次异常，由调用方兜底提示（钱已支付，不能静默吞掉失败）
 */
export const submitManualReviewWithRetry = async (
  action: () => Promise<void>,
  retryCount: number = AUTO_SUBMIT_RETRY_COUNT,
  delayMs: number = AUTO_SUBMIT_RETRY_DELAY
) => {
  for (let attempt = 1; attempt <= retryCount; attempt += 1) {
    try {
      await action()
      return
    } catch (error) {
      if (attempt === retryCount) {
        throw error
      }
      await new Promise((resolve) => setTimeout(resolve, delayMs))
    }
  }
}
